import { useEffect, useState, ChangeEvent, useCallback } from 'react';
import { Formik, Form, Field } from 'formik';
import { Col, Row } from 'react-bootstrap';
import * as yup from 'yup';
import { DefaultTheme, useTheme } from 'styled-components';
import { toast } from 'react-toastify';
import { serialize } from 'object-to-formdata';
import { useHistory, useParams } from 'react-router-dom';
import {
  FiUser,
  FiFacebook,
  FiTwitch,
  FiInstagram,
  FiTwitter,
  FiKey,
} from 'react-icons/fi';
import { Input } from '../../../components/Input';
import { Breadcrumb } from '../../../components/Breadcrumb';
import { ReactSelect } from '../../../components/ReactSelect';
import api from '../../../services/api';
import { ISelect } from '../../../interfaces/ISelect';
import { Button } from '../../../components/Button';
import { IFileImage } from '../../../interfaces/IFileImage';
import { UploadThumbnail } from '../../../components/UploadThumbnail';
import { IInfluencer } from '../../../interfaces/IInfluencer';

const dataInitial = {
  name: '',
  token: '',
  facebook: '',
  twitch: '',
  instagram: '',
  twitter: '',
  useTwitch: '0',
  games: [],
};

const schema = yup.object({
  name: yup.string().required('Digite o nome'),
  token: yup.string().nullable(),
  facebook: yup.string().nullable(),
  twitch: yup.string().nullable(),
  instagram: yup.string().nullable(),
  twitter: yup.string().nullable(),
  useTwitch: yup.string().required('Selecione uma alternativa'),
  games: yup
    .array()
    .required('Campo obrigatório')
    .min(1, 'Selecione no mínimo 1'),
});

interface SubmitProps {
  name: string;
  token: string;
  facebook: string;
  twitch: string;
  instagram: string;
  twitter: string;
  games: ISelect[];
  useTwitch: any;
}

export const FormInfluencer = () => {
  const theme: DefaultTheme = useTheme();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [gamesPreload, setGamesPreload] = useState<ISelect[]>([]);

  const [influencer, setInfluencer] = useState<IInfluencer>();
  const [loading, setLoading] = useState(false);
  const [thumbnail, setThumbnail] = useState<IFileImage>();

  const handleSubmit = async ({
    name,
    token,
    facebook,
    twitch,
    instagram,
    twitter,
    games,
    useTwitch,
  }: SubmitProps) => {
    try {
      setLoading(true);
      if (!thumbnail && !id) {
        toast.error('Adicione a foto');
        return;
      }
      const gamesArray = games.map(p => p.value).concat([]);
      const dataFormatted = {
        name,
        token,
        facebook,
        twitch,
        instagram,
        twitter,
        games: JSON.stringify(gamesArray),
        file: thumbnail?.file,
        useTwitch,
      };
      const formData = serialize(dataFormatted);
      if (id) {
        await api.put(`/influencers/${id}`, formData);
      } else {
        await api.post('/influencers', formData);
      }
      toast.success('Influenciador salvo com sucesso');
      history.push('/influencers');
    } catch (err: any) {
      if (err.response.data.message) {
        toast.error(err.response.data.message);
        return;
      }
      toast.error('Erro ao salvar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api
      .get('/games')
      .then(({ data }) =>
        setGamesPreload(
          data.map((item: any) => ({
            label: item.name,
            value: item.id,
          })),
        ),
      )
      .catch(() => toast.error('Erro ao buscar games'));
  }, []);

  useEffect(() => {
    if (id) {
      api
        .get(`/influencers/${id}`)
        .then(({ data }) => {
          const dataFormatted = {
            ...data,
            useTwitch: String(data.useTwitch),
            games: data.games
              .map((p: any) => ({
                label: p.name,
                value: p.id,
              }))
              .concat([]),
          };
          setInfluencer(dataFormatted);
        })
        .catch(() => toast.error('Erro ao buscar influenciador'));
    }
  }, [id]);

  const onSelectFile = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const url = URL.createObjectURL(e.target.files[0]);
      setThumbnail({ file: e.target.files[0], url });
    }
  }, []);

  return (
    <div>
      <Breadcrumb
        items={[
          {
            title: 'Influenciadores',
            link: '/influencers',
          },
          {
            title: 'Cadastro de Influenciadores',
          },
        ]}
      />
      <Row className="d-flex align-items-center mt-4">
        <Col md={10} xs={9}>
          <h3>Novo Influenciadores</h3>
        </Col>
      </Row>
      <Row className="mt-4">
        <Col md={{ span: 6, offset: 3 }}>
          <Formik
            initialValues={influencer || dataInitial}
            enableReinitialize
            onSubmit={handleSubmit}
            validationSchema={schema}
          >
            {({ errors }) => (
              <Form>
                <UploadThumbnail
                  onSubmit={onSelectFile}
                  urlThumbnail={thumbnail?.url || influencer?.thumb}
                />
                <Input
                  type="text"
                  name="name"
                  icon={FiUser}
                  placeholder="Nome do influenciador"
                />
                <Input
                  type="text"
                  name="facebook"
                  icon={FiFacebook}
                  placeholder="Facebook do influenciador"
                />
                <div className="mt-3">
                  <label htmlFor="" style={{ color: theme.colors.textPrimary }}>
                    Utiliza twitch?
                  </label>
                  <div className="d-flex">
                    <label
                      className="mb-0 d-block"
                      style={{ color: theme.colors.textPrimary }}
                    >
                      <Field type="radio" name="useTwitch" value="0" />
                      Não
                    </label>
                    <label
                      className="ml-2 mb-0 d-block"
                      style={{ color: theme.colors.textPrimary }}
                    >
                      <Field type="radio" name="useTwitch" value="1" />
                      Sim
                    </label>
                  </div>
                  {errors.useTwitch && <span>{errors.useTwitch}</span>}
                </div>
                <Input
                  type="text"
                  name="twitch"
                  icon={FiTwitch}
                  placeholder="Twitch do influenciador"
                />

                <Input
                  type="text"
                  name="token"
                  icon={FiKey}
                  placeholder="Token do influenciador na twitch"
                />

                <Input
                  type="text"
                  name="instagram"
                  icon={FiInstagram}
                  placeholder="Instagram do influenciador"
                />
                <Input
                  type="text"
                  name="twitter"
                  icon={FiTwitter}
                  placeholder="Twitter do influenciador"
                />

                <Field
                  name="games"
                  options={gamesPreload}
                  component={ReactSelect}
                  isMulti
                  placeholder="Games"
                />

                <Col md={{ span: 6, offset: 3 }}>
                  <Button
                    className="mt-4"
                    type="submit"
                    background={theme.colors.secondary}
                    loading={loading}
                    color="#fff"
                  >
                    Salvar
                  </Button>
                </Col>
              </Form>
            )}
          </Formik>
        </Col>
      </Row>
    </div>
  );
};
